import type {
    Assignment,
    DCULPath,
    Student,
    UserHasChildAssignmentClassrooms,
} from "@khan/assignments-types";
import {getContentTitle, getUniqueCount} from "@khan/assignments-utils";

import CreateAssignmentNotification from "./create-assignment-notification.tsx";
import UpdateAssignmentNotification from "./update-assignment-notification.tsx";

/**
 * Maps the assignment data to the mutation variables object.
 *
 * @param {Assignment} assignment The assignment info
 *
 */
// NOTE: Exported for testing purposes
export function assignmentToCommonVariables(assignment: Assignment): any {
    return {
        startDate: assignment.startDate,
        dueDate: assignment.dueDate,
        isDraft: assignment.isDraft,
    };
}

// NOTE: Exported for testing purposes
export function studentsDiff(
    oldStudents: Array<Student>,
    newStudents: Array<Student>,
): {
    added: Array<Student>;
    removed: Array<Student>;
} {
    if (!oldStudents) {
        return {added: newStudents, removed: []};
    }

    const originalStudentsMap = new Map(
        oldStudents.map((student) => [student.kaid, student]),
    );

    const added = newStudents.filter(
        (student) => !originalStudentsMap.has(student.kaid),
    );

    const newStudentsMap = new Map(
        newStudents.map((student) => [student.kaid, student]),
    );

    const removed = oldStudents.filter(
        (student) => !newStudentsMap.has(student.kaid),
    );

    return {added, removed};
}

/**
 * Exported for testing.
 */
export function getQualifiedDescriptor(
    contentDescriptor?: string | null,
    path?: DCULPath | null,
): string | null | undefined {
    if (!path) {
        return contentDescriptor;
    }
    const {domainId, courseId, unitId, lessonId, contentKind, contentId} = path;
    // Create a token with the ids, like: "x111:x222:x333:x444:Video:x555"
    return [domainId, courseId, unitId, lessonId, contentKind, contentId].join(
        ":",
    );
}

export const triggerCreateAssignmentNotification = (
    assignments: Array<Assignment>,
    createAction: "ASSIGN" | "SAVE",
    requestedContentTitle: string,
    requestedClassCount = 1,
    requestedContentCount = 1,
    totalStudentsInClass = 0,
) => {
    const successContentCount = getUniqueCount(
        assignments.map(
            (a) => (a.contents && a.contents[0] && a.contents[0].id) || "",
        ),
    );
    const successClassCount = getUniqueCount(
        assignments.map((a) => (a.studentList && a.studentList.id) || ""),
    );
    const studentList = assignments[0] && assignments[0].studentList;
    const studentCount = assignments[0] && assignments[0].students.length;
    const contentTitle =
        requestedContentTitle || getContentTitle(assignments[0]);
    const assignedToChildAssignmentsClassroom =
        getAssignedToChildAssignmentsClassrooms(assignments);

    import("@khan/component-toast").then(({renderToast}) => {
        renderToast(
            <CreateAssignmentNotification
                createAction={createAction}
                successContentCount={successContentCount}
                successClassCount={successClassCount}
                requestedClassCount={requestedClassCount}
                requestedContentCount={requestedContentCount}
                // eslint-disable-next-line @khanacademy/ts-no-error-suppressions -- TODO: fix type error
                // @ts-expect-error - TS2322 - name should not be nullable
                studentList={studentList ?? undefined}
                contentTitle={contentTitle}
                studentCount={studentCount}
                totalStudentsInClass={totalStudentsInClass}
                assignedToChildAssignmentsClassroom={
                    assignedToChildAssignmentsClassroom
                }
            />,
        );
    });
};

const getAssignedToChildAssignmentsClassrooms = (
    assignments: Array<Assignment>,
): UserHasChildAssignmentClassrooms => {
    let childAssignmentClassroomCount = 0;

    for (const assignment of assignments) {
        if (assignment.studentList?.isChildAssignmentsClassroom) {
            childAssignmentClassroomCount++;
        }
    }

    if (childAssignmentClassroomCount === assignments.length) {
        return "ALL";
    }

    if (childAssignmentClassroomCount > 1) {
        return "SOME";
    }

    return "NONE";
};

export const triggerDuplicateErrorToast = () => {
    import("@khan/component-toast").then(({renderToast}) => {
        renderToast(<CreateAssignmentNotification isDuplicateError={true} />);
    });
};

export const triggerUpdateAssignmentNotification = (assignment: Assignment) => {
    const contentTitle = getContentTitle(assignment);
    const studentListName =
        (assignment.studentList && assignment.studentList.name) || "";

    import("@khan/component-toast").then(({renderToast}) => {
        renderToast(
            <UpdateAssignmentNotification
                contentTitle={contentTitle}
                studentListName={studentListName}
                isChildAssignmentsClassroom={
                    !!assignment.studentList?.isChildAssignmentsClassroom
                }
            />,
        );
    });
};

export const triggerUpdateAssignmentsNotification = (
    contentTitle: string,
    studentListName = "",
    isChildAssignmentsClassroom: boolean,
) => {
    import("@khan/component-toast").then(({renderToast}) => {
        renderToast(
            <UpdateAssignmentNotification
                contentTitle={contentTitle}
                studentListName={studentListName}
                isChildAssignmentsClassroom={isChildAssignmentsClassroom}
            />,
        );
    });
};

export const triggerUpdateAssignmentErrorNotification = (
    contentTitle: string,
) => {
    import("@khan/component-toast").then(({renderToast}) => {
        renderToast(
            <UpdateAssignmentNotification
                contentTitle={contentTitle}
                isError={true}
                isChildAssignmentsClassroom={false}
            />,
        );
    });
};
